import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardSection, Badge, Button } from '@trafiflow/shared-ui'
import { Spinner } from '@heroui/react'
import { Search, Plus, ChevronDown, MoreVertical, X } from 'lucide-react'
import { alertsApi, type AlertEvent, type AlertRule } from '../lib/funnel-api'
import { getErrorMessage, type ApiError } from '../lib/api-error'
import { AlertTemplates } from '../components/alerts'
import { DialogPortal } from '../components/shared/DialogPortal'
import { inputClass, selectClass, labelClass } from '../styles/class-utils'

// Utilities
function getSeverityBadgeVariant(severity: string) {
  switch (severity) {
    case 'info': return 'default'
    case 'warning': return 'warning'
    case 'critical': return 'critical'
    default: return 'default'
  }
}

// --- Row Action Menu ---

function AlertRowMenu({ children }: { children: { label: string; onClick: () => void; danger?: boolean }[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-md hover:bg-fn-hover text-fn-text-muted hover:text-fn-text-sub transition-colors"
        aria-label="Actions"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-40 bg-fn-card rounded-lg shadow-fn-dropdown border border-fn-border py-1 z-50 animate-scale-in">
          {children.map((item) => (
            <button
              key={item.label}
              onClick={() => { item.onClick(); setOpen(false) }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${ item.danger ? 'text-red-600 hover:bg-tf-danger-light' : 'text-fn-text-sub hover:bg-fn-hover' }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// --- Active Alerts Tab ---

function useAlerts(severityFilter: string, readFilter: string) {
  const [events, setEvents] = useState<AlertEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchEvents = useCallback(async (params: Record<string, string>) => {
    try {
      setLoading(true)
      setError(null)
      const response = await alertsApi.listEvents(params)
      setEvents(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      setError(getErrorMessage(err as ApiError, 'Failed to load alert events.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const params: Record<string, string> = {}
      if (severityFilter) params.severity = severityFilter
      if (readFilter) params.isRead = readFilter
      fetchEvents(params)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [severityFilter, readFilter, fetchEvents])

  const handleMarkRead = useCallback(async (id: string) => {
    try {
      await alertsApi.markRead(id)
      const params: Record<string, string> = {}
      if (severityFilter) params.severity = severityFilter
      if (readFilter) params.isRead = readFilter
      await fetchEvents(params)
    } catch (err) {
      window.alert(getErrorMessage(err as ApiError, 'Failed to mark alert as read.'))
    }
  }, [severityFilter, readFilter, fetchEvents])

  return { events, loading, error, fetchEvents, handleMarkRead }
}

function ActiveAlertsTab() {
  const [searchQuery, setSearchQuery] = useState('')
  const [severityFilter, setSeverityFilter] = useState('')
  const [readFilter, setReadFilter] = useState('')
  const { events, loading, error, fetchEvents, handleMarkRead } = useAlerts(severityFilter, readFilter)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; eventId: string; isRead: boolean } | null>(null)

  useEffect(() => {
    if (!contextMenu) return
    const dismiss = () => setContextMenu(null)
    document.addEventListener('mousedown', dismiss)
    return () => document.removeEventListener('mousedown', dismiss)
  }, [contextMenu])

  const filteredEvents = useMemo(() => {
    const query = searchQuery.toLowerCase()
    return events.filter((event) =>
      !searchQuery || event.message.toLowerCase().includes(query) || event.id.toLowerCase().includes(query)
    )
  }, [events, searchQuery])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Spinner size="lg" color="primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center bg-tf-feedback-error-bg rounded-lg border border-tf-feedback-error-border">
        <p className="text-red-600 font-medium">{error}</p>
        <Button onClick={() => fetchEvents({ severity: severityFilter, isRead: readFilter })} variant="primary" className="mt-4">
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <Card>
        <CardSection className="p-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
              <input
                type="text"
                className={`${inputClass} pl-9`}
                placeholder="Search by message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="relative">
              <select
                className={selectClass}
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                aria-label="Filter by severity"
              >
                <option value="">All Severities</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
            </div>
            <div className="relative">
              <select
                className={selectClass}
                value={readFilter}
                onChange={(e) => setReadFilter(e.target.value)}
                aria-label="Filter by read status"
              >
                <option value="">All Status</option>
                <option value="false">Unread</option>
                <option value="true">Read</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
            </div>
          </div>
        </CardSection>
      </Card>
      <div className="text-sm text-fn-text-muted">
        {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
      </div>
      <Card>
        <CardSection className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fn-border bg-fn-muted">
                  <th className="text-left px-4 py-3 font-medium text-fn-text-muted uppercase text-xs tracking-wide">Severity</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-muted uppercase text-xs tracking-wide">Message</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-muted uppercase text-xs tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-muted uppercase text-xs tracking-wide">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-muted uppercase text-xs tracking-wide w-16">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-fn-text-muted">
                      No alert events found.
                    </td>
                  </tr>
                ) : (
                  filteredEvents.map((event) => (
                    <tr
                      key={event.id}
                      className="border-b border-fn-border hover:bg-fn-hover transition-colors animate-fade-slide-in"
                      onContextMenu={(e) => {
                        if (event.isRead) return
                        e.preventDefault()
                        setContextMenu({ x: e.clientX, y: e.clientY, eventId: event.id, isRead: event.isRead })
                      }}
                    >
                      <td className="px-4 py-3">
                        <Badge variant={getSeverityBadgeVariant(event.severity)}>{event.severity}</Badge>
                      </td>
                      <td className="px-4 py-3 text-fn-text">{event.message}</td>
                      <td className="px-4 py-3">
                        <Badge variant={event.isRead ? 'default' : 'success'}>{event.isRead ? 'Read' : 'New'}</Badge>
                      </td>
                      <td className="px-4 py-3 text-fn-text-muted">
                        {new Date(event.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        {!event.isRead && (
                          <AlertRowMenu>
                            {[{ label: 'Mark Read', onClick: () => handleMarkRead(event.id) }]}
                          </AlertRowMenu>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardSection>
      </Card>
      {contextMenu && !contextMenu.isRead && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setContextMenu(null) }} />
          <div
            className="fixed w-40 bg-fn-card rounded-lg shadow-fn-dropdown border border-fn-border py-1 z-50 animate-scale-in"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            <button
              onClick={() => { handleMarkRead(contextMenu.eventId); setContextMenu(null) }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-fn-text-sub hover:bg-fn-hover transition-colors"
            >
              Mark Read
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// --- Alert Rules Tab ---

interface RuleFormData {
  name: string
  entityType: string
  triggerAction: string
  severity: string
}

function useAlertRules() {
  const [rules, setRules] = useState<AlertRule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await alertsApi.listRules()
      setRules(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      setError(getErrorMessage(err as ApiError, 'Failed to load alert rules.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchRules() }, [fetchRules])

  const handleDelete = useCallback(async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this alert rule?')) return
    try {
      await alertsApi.deleteRule(id)
      await fetchRules()
    } catch (err) {
      window.alert(getErrorMessage(err as ApiError, 'Failed to delete alert rule.'))
    }
  }, [fetchRules])

  const handleToggleActive = useCallback(async (rule: AlertRule) => {
    try {
      await alertsApi.updateRule(rule.id, { isActive: !rule.isActive })
      await fetchRules()
    } catch (err) {
      window.alert(getErrorMessage(err as ApiError, 'Failed to update alert rule.'))
    }
  }, [fetchRules])

  return { rules, loading, error, fetchRules, handleDelete, handleToggleActive }
}

function AlertRulesTab() {
  const { rules, loading, error, fetchRules, handleDelete, handleToggleActive } = useAlertRules()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [formData, setFormData] = useState<RuleFormData>({
    name: '', entityType: 'funnel', triggerAction: 'created', severity: 'info',
  })
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; ruleId: string } | null>(null)

  useEffect(() => {
    if (!contextMenu) return
    const dismiss = () => setContextMenu(null)
    document.addEventListener('mousedown', dismiss)
    return () => document.removeEventListener('mousedown', dismiss)
  }, [contextMenu])

  const openCreateModal = () => {
    setFormData({ name: '', entityType: 'funnel', triggerAction: 'created', severity: 'info' })
    setFormError(null)
    setIsModalOpen(true)
  }

  const handleCreateRule = async () => {
    if (!formData.name.trim()) { setFormError('Name is required.'); return }
    setSaving(true)
    setFormError(null)
    try {
      await alertsApi.createRule(formData)
      await fetchRules()
      setIsModalOpen(false)
    } catch (err) {
      setFormError(getErrorMessage(err as ApiError, 'Failed to create alert rule.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Spinner size="lg" color="primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center bg-tf-feedback-error-bg rounded-lg border border-tf-feedback-error-border">
        <p className="text-red-600 font-medium">{error}</p>
        <Button onClick={fetchRules} variant="primary" className="mt-4">Retry</Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <p className="text-sm text-fn-text-muted">{rules.length} rule{rules.length !== 1 ? 's' : ''}</p>
        <Button variant="primary" size="sm" onClick={openCreateModal}>
          <Plus className="w-4 h-4 mr-2" />Create Rule
        </Button>
      </div>
      <Card>
        <CardSection className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fn-border bg-fn-muted">
                  <th className="text-left px-4 py-3 font-medium text-fn-text-muted uppercase text-xs tracking-wide">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-muted uppercase text-xs tracking-wide">Entity Type</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-muted uppercase text-xs tracking-wide">Trigger</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-muted uppercase text-xs tracking-wide">Severity</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-muted uppercase text-xs tracking-wide">Active</th>
                  <th className="text-left px-4 py-3 font-medium text-fn-text-muted uppercase text-xs tracking-wide w-16">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-fn-text-muted">
                      No alert rules yet. Create your first rule.
                    </td>
                  </tr>
                ) : (
                  rules.map((rule) => (
                    <tr
                      key={rule.id}
                      className="border-b border-fn-border hover:bg-fn-hover transition-colors animate-fade-slide-in"
                      onContextMenu={(e) => {
                        e.preventDefault()
                        setContextMenu({ x: e.clientX, y: e.clientY, ruleId: rule.id })
                      }}
                    >
                      <td className="px-4 py-3 font-medium text-fn-text">{rule.name}</td>
                      <td className="px-4 py-3"><Badge variant="default">{rule.entityType}</Badge></td>
                      <td className="px-4 py-3"><Badge variant="default">{rule.triggerAction}</Badge></td>
                      <td className="px-4 py-3"><Badge variant={getSeverityBadgeVariant(rule.severity)}>{rule.severity}</Badge></td>
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rule.isActive}
                            onChange={() => handleToggleActive(rule)}
                            className="sr-only peer"
                          />
                          <div className="w-8 h-[18px] bg-tf-bg-hover peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-[14px] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-tf-bg-card after:border-tf-border after:border after:rounded-full after:h-3.5 after:w-3.5 after:transition-all peer-checked:bg-primary" />
                        </label>
                      </td>
                      <td className="px-4 py-3">
                        <AlertRowMenu>
                          {[{ label: 'Delete', onClick: () => handleDelete(rule.id), danger: true }]}
                        </AlertRowMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardSection>
      </Card>
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setContextMenu(null) }} />
          <div
            className="fixed w-40 bg-fn-card rounded-lg shadow-fn-dropdown border border-fn-border py-1 z-50 animate-scale-in"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            <button
              onClick={() => { handleDelete(contextMenu.ruleId); setContextMenu(null) }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-tf-danger-light transition-colors"
            >
              Delete
            </button>
          </div>
        </>
      )}
      {isModalOpen && (
        <DialogPortal>
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 animate-fade-in" onClick={() => setIsModalOpen(false)} />
          <div className="relative z-10 w-full max-w-lg bg-fn-card rounded-lg shadow-fn-modal border border-fn-border animate-scale-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-fn-border">
              <div>
                <h2 className="text-lg font-semibold text-fn-text">Create Alert Rule</h2>
                <p className="text-sm text-fn-text-muted">Add a new alert notification rule</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="p-1 rounded hover:bg-fn-hover transition-colors">
                <X className="w-5 h-5 text-fn-text-muted" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {formError && (
                <div className="px-3 py-2 text-sm text-red-600 bg-tf-feedback-error-bg border border-tf-feedback-error-border rounded-md">
                  {formError}
                </div>
              )}
              <div>
                <label className={labelClass}>
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={inputClass}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter rule name"
                />
              </div>
              <div>
                <label className={labelClass}>Entity Type</label>
                <div className="relative">
                  <select
                    className={selectClass}
                    value={formData.entityType}
                    onChange={(e) => setFormData({ ...formData, entityType: e.target.value })}
                  >
                    <option value="funnel">Funnel</option>
                    <option value="offer">Offer</option>
                    <option value="step">Step</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
                </div>
              </div>
              <div>
                <label className={labelClass}>Trigger Action</label>
                <div className="relative">
                  <select
                    className={selectClass}
                    value={formData.triggerAction}
                    onChange={(e) => setFormData({ ...formData, triggerAction: e.target.value })}
                  >
                    <option value="created">Created</option>
                    <option value="updated">Updated</option>
                    <option value="deleted">Deleted</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
                </div>
              </div>
              <div>
                <label className={labelClass}>Severity</label>
                <div className="relative">
                  <select
                    className={selectClass}
                    value={formData.severity}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                  >
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="critical">Critical</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-fn-border">
              <Button variant="secondary" onClick={() => setIsModalOpen(false)} disabled={saving}>Cancel</Button>
              <Button variant="primary" onClick={handleCreateRule} disabled={saving}>
                {saving ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
        </DialogPortal>
      )}
    </div>
  )
}

// --- Main Alerts Page with 3 Tabs ---

const TAB_CONFIG = [
  { key: 'templates', label: 'Alert Templates' },
  { key: 'active', label: 'Active Alerts' },
  { key: 'rules', label: 'Alert Rules' },
] as const

export default function Alerts() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTab = searchParams.get('tab') || 'templates'
  const [activeTab, setActiveTab] = useState(initialTab)

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setSearchParams({ tab }, { replace: true })
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-tf-text">Alerts & Monitoring</h1>
        <p className="text-tf-text-muted">Manage alert templates, view active alerts, and configure rules</p>
      </div>

      <div className="border-b border-fn-border">
        <nav className="flex gap-6" aria-label="Tabs">
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${ activeTab === tab.key ? 'border-primary text-primary' : 'border-transparent text-fn-text-muted hover:text-fn-text-sub hover:border-tf-border' }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div>
        {activeTab === 'templates' && <AlertTemplates />}
        {activeTab === 'active' && <ActiveAlertsTab />}
        {activeTab === 'rules' && <AlertRulesTab />}
      </div>
    </div>
  )
}
